/**
 * useDashboard Hook
 * Custom hook for dashboard data fetching and real-time updates
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';

import { getApiBaseUrl } from '../config/api';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { getAuthToken } from '../services/authTokenStore';
import { useDashboardStore } from '../stores/dashboardStore';

interface UseDashboardOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useDashboard(options: UseDashboardOptions = {}) {
  const { enabled = true, refetchInterval = 30000 } = options;
  const queryClient = useQueryClient();
  const apiBase = getApiBaseUrl();

  // Use specific selectors to prevent unnecessary re-renders
  const counts = useDashboardStore(state => state.counts);
  const trends = useDashboardStore(state => state.trends);
  const isStoreLoading = useDashboardStore(state => state.isLoading);
  const storeError = useDashboardStore(state => state.error);
  const setAgentCounts = useDashboardStore(state => state.setAgentCounts);
  const setAgentTrends = useDashboardStore(state => state.setAgentTrends);
  const setStoreError = useDashboardStore(state => state.setError);
  const clearStoreError = useDashboardStore(state => state.clearError);
  const resetStore = useDashboardStore(state => state.reset);

  const { subscribe, unsubscribe, isConnected } = useWebSocketContext();

  // Fetch initial dashboard data
  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch(`${apiBase}/api/v1/agents?summary=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        return {
          counts: { total: 0, online: 0, offline: 0, error: 0 },
          trends: { total: 0, online: 0, offline: 0, error: 0 },
        };
      }
      try {
        return await response.json();
      } catch {
        return {
          counts: { total: 0, online: 0, offline: 0, error: 0 },
          trends: { total: 0, online: 0, offline: 0, error: 0 },
        };
      }
    },
    enabled,
    refetchInterval,
    staleTime: 10000,
  });

  // Fetch system health
  const { data: healthData } = useQuery({
    queryKey: ['health', 'system'],
    queryFn: async () => {
      // /api/v1/health/system is not available in all environments.
      // Use the stable /health endpoint and avoid noisy console errors.
      const response = await fetch(`${apiBase}/health`);
      if (!response.ok) {
        return { status: 'unknown', modules: 0 };
      }
      try {
        return await response.json();
      } catch {
        return { status: 'unknown', modules: 0 };
      }
    },
    enabled,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Handle agent status updates from WebSocket
  const handleAgentStatusUpdate = useCallback(
    (data: any) => {
      if (data.counts) {
        setAgentCounts(data.counts);
      }
      if (data.trends) {
        setAgentTrends(data.trends);
      }
      // Invalidate agent list cache
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    [setAgentCounts, setAgentTrends, queryClient]
  );

  // Handle metrics updates from WebSocket
  const handleMetricsUpdate = useCallback(
    (data: any) => {
      // Update React Query cache with new metrics
      queryClient.setQueryData(['dashboard', 'metrics'], data);
    },
    [queryClient]
  );

  // Handle alert updates from WebSocket
  const handleAlertUpdate = useCallback(
    (data: any) => {
      // Add alert to cache
      queryClient.setQueryData(['alerts', 'recent'], (old: any) => {
        if (!old) return [data];
        return [data, ...old].slice(0, 50);
      });
    },
    [queryClient]
  );

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isConnected) return;

    const subscriptions: string[] = [];

    try {
      // Subscribe to agent status updates
      const agentStatusSub = subscribe('agent.status', handleAgentStatusUpdate);
      subscriptions.push(agentStatusSub);

      // Subscribe to metrics updates
      const metricsSub = subscribe('metrics', handleMetricsUpdate);
      subscriptions.push(metricsSub);

      // Subscribe to alerts
      const alertsSub = subscribe('alerts', handleAlertUpdate);
      subscriptions.push(alertsSub);
    } catch (err) {
      console.error('Failed to subscribe to WebSocket topics:', err);
    }

    // Cleanup subscriptions on unmount
    return () => {
      subscriptions.forEach(sub => {
        try {
          unsubscribe(sub);
        } catch (err) {
          console.error('Failed to unsubscribe:', err);
        }
      });
    };
  }, [
    isConnected,
    subscribe,
    unsubscribe,
    handleAgentStatusUpdate,
    handleMetricsUpdate,
    handleAlertUpdate,
  ]);

  // Update store with initial data
  useEffect(() => {
    if (dashboardData) {
      setAgentCounts(
        dashboardData.counts || {
          total: 0,
          online: 0,
          offline: 0,
          error: 0,
        }
      );
      setAgentTrends(
        dashboardData.trends || {
          total: 0,
          online: 0,
          offline: 0,
          error: 0,
        }
      );
    }
  }, [dashboardData, setAgentCounts, setAgentTrends]);

  // Handle errors
  useEffect(() => {
    if (error) {
      setStoreError(error as Error);
    } else {
      clearStoreError();
    }
  }, [error, setStoreError, clearStoreError]);

  return {
    // State
    counts,
    trends,
    isLoading: isLoading || isStoreLoading,
    error: error || storeError,
    healthData,
    isConnected,

    // Actions
    refetch,
    clearError: clearStoreError,
    reset: resetStore,
  };
}
